import axios from 'axios';
import { API_CONFIG } from '../config/env';

export interface PaymentLinkRequest {
  fiatCurrency: string;
  fiatAmount: number;
  userId: string;
  title?: string;
  description?: string;
}

export interface GenerateLinkRequest {
  userId: number;
  fiatCurrency: string;
  amount: number;
  payerCountryCode?: string;
  payerSubdivisionCode?: string;
  selectedProviders?: string[];
  selectedProviderOptions?: AvailableProviderOption[];
  merchantReferences?: MerchantReferences;
  invoice?: InvoiceDetails;
}

export interface GenerateLinkResponse {
  transactionId: number;
  paymentPageUrl: string;
  paymentLinks: string[];
  validatedProviders: ValidatedProviderSummary[];
  uniqueWalletAddress: string;
  disposableAddress?: string;
  isInvoice: boolean;
  invoiceNumber?: string | null;
  merchantReferences?: MerchantReferences | null;
}

export interface ValidatedProviderSummary {
  providerKey: string;
  providerName: string;
  displayName: string;
  source: string;
  paymentMethod: string;
}

export interface MerchantReferences {
  merchantTransactionId?: string | null;
  merchantUserId?: string | null;
  merchantSessionId?: string | null;
  idempotencyKey?: string | null;
  metadata?: Record<string, string | null>;
}

export interface InvoiceDetails {
  invoiceNumber: string;
  issueDate?: string | null;
  dueDate?: string | null;
  taxOrVat?: string;
  taxType?: '' | 'Tax' | 'VAT' | null;
  taxPercent?: number | null;
  issuerCompanyName: string;
  issuerCompanyAddress: string;
  issuerCompanyNumber: string;
  issuerTaxOrVatNumber?: string;
  payingCompanyName: string;
  payingCompanyAddress: string;
  payingCompanyNumber: string;
  payingTaxOrVatNumber?: string;
  serviceDescription?: string;
  items: InvoiceLineItem[];
  notes?: string;
  subtotal?: number;
  taxAmount?: number;
  total?: number;
}

export interface InvoiceLineItem {
  itemName: string;
  quantity: number;
  unitPrice: number;
  amount?: number;
}

export interface AvailableProviderOption {
  providerKey: string;
  providerName: string;
  displayName: string;
  source: string;
  paymentMethod: string;
  isRecommended: boolean;
}

interface MinimumFiatAmountResponse {
  fiatCurrency: string;
  minimumFiatAmount: number;
  minimumFiatAmountCurrency: string;
}

export interface PaymentLinkResponse {
  uniqueHash: string;
  paymentUrl: string;
  createdAt: string;
  expiresAt?: string;
  status: 'active' | 'inactive' | 'expired';
}

export interface PaymentLink {
  id: string;
  uniqueHash: string;
  paymentUrl: string;
  fiatCurrency: string;
  fiatAmount: number;
  userId: string;
  userName: string;
  userRole: string;
  title?: string;
  description?: string;
  status: 'active' | 'inactive' | 'expired';
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  clickCount: number;
  conversionCount: number;
}

class PaymentLinkService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Create a new payment link
   */
  async createPaymentLink(data: PaymentLinkRequest): Promise<PaymentLinkResponse> {
    try {
      const response = await axios.post<PaymentLinkResponse>(`${API_CONFIG.BASE_URL}/payment-links`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error creating payment link:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Get all payment links for the current user
   */
  async getPaymentLinks(): Promise<PaymentLink[]> {
    try {
      const response = await axios.get<PaymentLink[]>(`${API_CONFIG.BASE_URL}/payment-links`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching payment links:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Get a specific payment link by unique hash
   */
  async getPaymentLink(uniqueHash: string): Promise<PaymentLink> {
    try {
      const response = await axios.get<PaymentLink>(`${API_CONFIG.BASE_URL}/payment-links/${uniqueHash}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching payment link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Update a payment link
   */
  async updatePaymentLink(uniqueHash: string, data: Partial<PaymentLinkRequest>): Promise<PaymentLink> {
    try {
      const response = await axios.put<PaymentLink>(`${API_CONFIG.BASE_URL}/payment-links/${uniqueHash}`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating payment link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Delete a payment link
   */
  async deletePaymentLink(uniqueHash: string): Promise<void> {
    try {
      await axios.delete(`${API_CONFIG.BASE_URL}/payment-links/${uniqueHash}`, {
        headers: this.getAuthHeaders(),
      });
    } catch (error) {
      console.error(`Error deleting payment link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Get payment link analytics
   */
  async getPaymentLinkAnalytics(uniqueHash: string): Promise<{
    clickCount: number;
    conversionCount: number;
    conversionRate: number;
    totalRevenue: number;
    lastClickedAt?: string;
  }> {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/payment-links/${uniqueHash}/analytics`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching analytics for payment link ${uniqueHash}:`, error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  /**
   * Generate payment links using the new endpoint
   */
  async generatePaymentLinks(data: GenerateLinkRequest): Promise<GenerateLinkResponse> {
    try {
      const response = await axios.post<GenerateLinkResponse>(`${API_CONFIG.BASE_URL}/Transactions/generate_link`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error generating payment links:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  async getAvailableProviders(
    fiatCurrency: string,
    amount: number,
    payerCountryCode?: string,
    payerSubdivisionCode?: string
  ): Promise<AvailableProviderOption[]> {
    const response = await axios.post<AvailableProviderOption[]>(
      `${API_CONFIG.BASE_URL}/Transactions/providers`,
      {
        fiatCurrency,
        amount,
        payerCountryCode,
        payerSubdivisionCode,
      },
      {
        headers: this.getAuthHeaders(),
      }
    );

    return response.data;
  }

  async getMinimumFiatAmount(fiatCurrency: string): Promise<number> {
    const response = await axios.get<MinimumFiatAmountResponse>(
      `${API_CONFIG.BASE_URL}/Transactions/minimum-fiat-amount`,
      {
        headers: this.getAuthHeaders(),
        params: {
          fiatCurrency,
        },
      }
    );

    if (typeof response.data?.minimumFiatAmount !== 'number' || Number.isNaN(response.data.minimumFiatAmount)) {
      throw new Error('Invalid minimum fiat amount response.');
    }

    return response.data.minimumFiatAmount;
  }

  /**
   * Generate a mock payment link for testing
   */
  generateMockPaymentLink(data: PaymentLinkRequest): PaymentLinkResponse {
    const uniqueHash = `PAY_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const paymentUrl = `${window.location.origin}/pay/${uniqueHash}`;
    
    return {
      uniqueHash,
      paymentUrl,
      createdAt: new Date().toISOString(),
      status: 'active'
    };
  }
}

export const paymentLinkService = new PaymentLinkService();
